import type { ChauffeurPage } from "../slug-chauffeur";

import heroImage from "@/assets/images/chauffeur/events/event-soir.png";
import { cannesEventPriceFaq, cannesEventSeo } from "./eventFaq";

const cannesLions: ChauffeurPage = {
  slug: "cannes-lions",
  title: "Chauffeur privé Cannes Lions – VTC agences",
  subtitle:
    "Chauffeur privé pendant les Cannes Lions : navettes équipes agences, déposes beach clubs et quais yachts autour du Palais des Festivals.",
  keywords: [
    "chauffeur cannes lions",
    "vtc cannes lions",
    "chauffeur privé cannes lions",
  ],
  hero: {
    title: "Chauffeur privé pendant les Cannes Lions",
    subtitle:
      "Festival de la créativité, en juin : un chauffeur pour les équipes agences entre Palais, beach clubs et yachts.",
    keywords: [
      "chauffeur Cannes Lions",
      "VTC agences Cannes",
      "chauffeur beach club Croisette",
    ],
    imageSrc: heroImage.src,
    imageAlt: "Chauffeur privé pendant les Cannes Lions",
    badges: ["Navettes équipes agences", "Déposes beach clubs", "Quais yachts"],
  },
  benefits: {
    heading: "Pourquoi un chauffeur pendant les Cannes Lions",
    items: [
      {
        title: "Agenda créatif chargé",
        description:
          "Conférences au Palais, masterclasses, activations de marque et remises de prix : un chauffeur dédié pour ne rien manquer.",
      },
      {
        title: "Beach clubs & yachts",
        description:
          "Plages privatisées par les plateformes et yachts amarrés au Vieux-Port : déposes au plus près des activations.",
      },
      {
        title: "Équipes & talents",
        description:
          "Navettes pour collaborateurs d'agences, clients et intervenants, avec flux séparés selon les besoins.",
      },
      {
        title: "Soirées tardives",
        description:
          "Parties de marques et afters sur la Croisette jusqu'au bout de la nuit : retour assuré, chauffeur qui patiente.",
      },
    ],
  },
  services: {
    heading: "Services chauffeur pendant les Cannes Lions",
    services: [
      {
        title: "Transfert aéroport de Nice",
        text: "Liaison Nice ↔ Cannes avec suivi de vol pendant l'afflux du festival en juin.",
      },
      {
        title: "Navettes équipes agences",
        text: "Coordination multi-véhicules pour collaborateurs, clients et délégations entre hôtels et Palais.",
      },
      {
        title: "Déposes beach clubs",
        text: "Approche au plus près des plages privatisées et des activations de marque sur la Croisette.",
      },
      {
        title: "Quais yachts",
        text: "Dépose et reprise au Vieux-Port de Cannes pour les yacht parties et rendez-vous business.",
      },
      {
        title: "Mise à disposition journée",
        text: "Chauffeur dédié à l'heure ou à la journée pour enchaîner conférences, activations et soirées.",
      },
    ],
  },
  locations: {
    heading: "Points clés pendant les Cannes Lions",
    items: [
      {
        name: "Palais des Festivals et des Congrès",
        description: "Conférences, masterclasses et cérémonie des Lions.",
      },
      {
        name: "Plages de la Croisette",
        description: "Beach clubs privatisés par les grandes plateformes.",
      },
      {
        name: "Vieux-Port de Cannes",
        description:
          "Quais des yachts privatisés pour les parties et rendez-vous.",
      },
      {
        name: "Aéroport de Nice Côte d'Azur",
        description: "Terminaux 1 et 2, afflux créatif concentré en juin.",
      },
      {
        name: "La Croisette",
        description: "Palaces, terrasses et activations de marque.",
      },
    ],
  },
  generic: {
    heading: "Les Cannes Lions vus d'un chauffeur",
    subheading:
      "En juin, le festival de la créativité étale Cannes du Palais aux beach clubs et aux yachts du Vieux-Port.",
    paragraphs: [
      "Chaque mois de juin, le Cannes Lions International Festival of Creativity rassemble agences, marques, médias et tech au Palais des Festivals. Mais l'essentiel se joue aussi sur les plages privatisées de la Croisette et sur les yachts amarrés au Vieux-Port, où conférences et activations s'enchaînent à un rythme soutenu.",
      "Les équipes d'agences et leurs clients arrivent en nombre à l'aéroport de Nice Côte d'Azur. Suivi de vol, accueil pancarte et navettes coordonnées fluidifient les premiers et derniers jours du festival, souvent les plus chargés.",
      "La mise à disposition garde le même chauffeur sur la journée et la soirée : déposes aux beach clubs, quais de yachts, dîners et parties de marques jusqu'au bout de la nuit, sans réserver à chaque trajet.",
    ],
    bullets: [
      "Déposes beach clubs",
      "Quais yachts Vieux-Port",
      "Afters jusqu'à l'aube",
    ],
  },
  process: {
    heading: "Réserver un chauffeur pour les Cannes Lions",
    steps: [
      "Indiquez vos dates, vos hôtels et vos lieux d'activation (Palais, plages, yachts).",
      "Nous coordonnons les navettes et les déposes au plus près des beach clubs et quais.",
      "Le chauffeur se présente en tenue, avec suivi de vol pour vos arrivées à Nice.",
      "Mise à disposition journée et soirée pour conférences, activations et parties.",
    ],
  },
  faq: [
    ...cannesEventPriceFaq("fr", "pendant les Cannes Lions"),
    {
      q: "Comment réserver un chauffeur pendant les Cannes Lions ?",
      a: "Réservez tôt : mi-juin, Cannes affiche complet et la demande de chauffeurs est forte. Transmettez vos dates, vos hôtels et vos lieux d'activation pour que nous bloquions un chauffeur dédié et coordonnions les navettes.",
    },
    {
      q: "Pouvez-vous déposer aux beach clubs et sur les quais des yachts ?",
      a: "Oui. Pendant le festival, de nombreuses plages de la Croisette et des yachts du Vieux-Port sont privatisés par les marques. Le chauffeur dépose au plus près de l'activation et vous reprend à la sortie.",
    },
    {
      q: "Gérez-vous les navettes pour une équipe d'agence complète ?",
      a: "Oui, nous coordonnons des flottes multi-véhicules pour collaborateurs, clients et intervenants, avec des flux séparés entre hôtels, Palais et lieux de soirée.",
    },
    {
      q: "Assurez-vous les retours après les parties de marques ?",
      a: "Oui. Les afters du Cannes Lions se prolongent tard : le chauffeur patiente et garantit votre retour de nuit vers l'hôtel.",
    },
  ],
  relatedLinks: [
    {
      label: "Liaison Cannes ⇄ Aéroport de Nice",
      href: "/chauffeur/transferts/cannes-aeroport-nice",
    },
    {
      label: "Liaison Cannes ⇄ Saint-Tropez",
      href: "/chauffeur/transferts/cannes-saint-tropez",
    },
    {
      label: "Réserver un véhicule avec chauffeur",
      href: "/reservation",
    },
  ],
  cta: {
    title: "Votre chauffeur pour les Cannes Lions",
    text: "Navettes agences, beach clubs et yachts : réservez un chauffeur dédié pour le festival.",
    primaryHref: "/reservation",
    primaryLabel: "Réserver un chauffeur",
  },
  seo: {
    // MESURE 2026-10-09 : page événement sans prix, même recette que le Festival.
    ...cannesEventSeo("fr", "Cannes Lions", "Beach clubs et quais des yachts."),
    canonical: "/chauffeur/cannes-lions",
  },
};

export default cannesLions;
